import React, { useEffect, useRef, useState } from "react";
import { useGetCompaniesQuery } from "@redux/features/companies/companiesApi";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { Fade, LinearProgress, Menu } from "@mui/material";
import { Company } from "@/types/general";
import { switchCompany } from "@redux/features/auth/authSlice";
import { cn, toastError } from "@helpers/utils";

import { ReactComponent as CompanyIconSVG } from "@assets/icons/ic_company.svg";
import { useTranslation } from "react-i18next";
import CompanyCreationModal from "./CompanyCreationModal";

const imageDomainURLPrefix = import.meta.env.VITE_API_URL;

export default function SidebarCompanySwitcher() {
    const { t } = useTranslation("", { keyPrefix: "dashboard.companySwitcher" });

    const dispatch = useAppDispatch();

    const { data: companiesOptions, isLoading: companiesOptionsLoading } = useGetCompaniesQuery();
    const activeCompany = useAppSelector(state => state.auth.user?.company);

    const anchorEl = React.useRef<HTMLDivElement>(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const [companyIdSwitchLoading, setCompanyIdSwitchLoading] = useState<number | undefined>();

    const handleClick = () => {
        if (!companiesOptionsLoading) {
            setIsMenuOpen(true);
        }
    };

    const handleClose = () => {
        setIsMenuOpen(false);
    };

    const handleSelectedCompanyChange = async (company: Company.Root) => {
        if (!company || companyIdSwitchLoading) return;

        try {
            setCompanyIdSwitchLoading(company.id);

            await dispatch(switchCompany(company.id));

            handleClose();
        } catch (e) {
            toastError(e as any);
        } finally {
            setCompanyIdSwitchLoading(undefined);
        }
    };

    const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);

    // The timer is used to predict miss-click and show the menu only if the user closed the modal after a short period of time
    const creationModalOpenTime = useRef(0);
    const creationModalOpenInterval = useRef<NodeJS.Timer | null>(null);

    useEffect(() => {
        if (isCreationModalOpen) {
            creationModalOpenTime.current = 0;

            creationModalOpenInterval.current = setInterval(() => {
                creationModalOpenTime.current += 1;
            }, 1000);
        }

        return () => {
            if (creationModalOpenInterval.current) {
                clearInterval(creationModalOpenInterval.current);
            }
        };
    }, [isCreationModalOpen]);

    const handleCreationModalOpen = () => {
        setIsCreationModalOpen(true);
        setIsMenuOpen(false);
    };

    const handleCreationModalClose = () => {
        setIsCreationModalOpen(false);

        // Predicting miss-click and showing the menu only if the user closed the modal after a short period of time
        if (creationModalOpenTime.current < 3) {
            setIsMenuOpen(true);
        }
    };

    return (
        <>
            <div className="flex flex-row">
                <div
                    className={cn(
                        "w-full grow flex items-center justify-between px-[12px] py-[8px] rounded-[8px] text-accent whitespace-nowrap hover:bg-white cursor-pointer border-solid border-[1px] border-transparent hover:border-gray-200 transition-[.25s]",
                        isMenuOpen && "bg-white border-gray-200"
                    )}
                    onClick={handleClick}
                >
                    <div className="flex items-center space-x-[8px] overflow-hidden">
                        {
                            activeCompany?.logo
                                ? <div
                                    style={{
                                        // Use flex-shrink-0 to prevent the logo/icon from shrinking
                                        flexShrink: 0,
                                        width: "20px",
                                        height: "20px",
                                        backgroundImage: `url(${imageDomainURLPrefix + activeCompany.logo})`,
                                        backgroundRepeat: "no-repeat",
                                        backgroundPosition: "center",
                                        backgroundSize: "cover",
                                        borderRadius: "4px"
                                    }}
                                    aria-label={`${activeCompany.name} logo`}
                                />
                                :
                                <div className="flex-shrink-0 text-[#cdcefa] mt-[-4px]">
                                    <CompanyIconSVG style={{ width: "16px", height: "16px" }} />
                                </div>
                        }
                        <span className="truncate">{activeCompany?.name}</span>
                    </div>
                    <div className="flex-shrink-0">
                        <ChevronsUpDown size={16} />
                    </div>
                </div>

                <div ref={anchorEl}>
                </div>

                <Menu
                    id="company-menu"
                    anchorEl={anchorEl.current}
                    open={isMenuOpen}
                    onClose={handleClose}
                    MenuListProps={{
                        "aria-labelledby": "company-button"
                    }}
                    sx={{
                        borderRadius: "20px",
                        marginLeft: "10px",
                        "& .MuiMenu-paper": {
                            boxShadow: "0px 0px 30px 0px #3B41D00D !important"
                        }
                    }}
                    PaperProps={{
                        sx: {
                            width: "300px"
                        },
                        className: "border border-gray-200"
                    }}
                    transformOrigin={{ horizontal: "left", vertical: "top" }}
                    anchorOrigin={{ horizontal: "left", vertical: "top" }}
                    TransitionComponent={Fade}
                >
                    <div className="py-[24px] px-[14px]">
                        <ul className="space-y-[4px] mb-2">
                            {
                                companiesOptions?.map((company) => {
                                    const isActive = company.id === activeCompany?.id;
                                    const isSwitchLoading = companyIdSwitchLoading === company.id;

                                    return (
                                        <li
                                            className="flex items-center py-[8px] space-x-[8px] text-accent relative px-[10px] hover:bg-[#F9F9F9] rounded-[8px] cursor-pointer"
                                            key={company.id}
                                            onClick={() => handleSelectedCompanyChange(company)}
                                        >
                                            {
                                                isSwitchLoading && (
                                                    <LinearProgress
                                                        className={"top-0 left-0 !absolute w-full !h-full rounded opacity-20"} />
                                                )
                                            }
                                            <div className="!ml-0">
                                                {company.logo ? (
                                                    <div
                                                        style={{
                                                            width: "20px",
                                                            height: "20px",
                                                            backgroundImage: `url(${imageDomainURLPrefix + company.logo})`,
                                                            backgroundRepeat: "no-repeat",
                                                            backgroundPosition: "center",
                                                            backgroundSize: "cover",
                                                            borderRadius: "4px"
                                                        }}
                                                        aria-label={`${company.name} logo`}
                                                    />
                                                ) : (
                                                    <div
                                                        className="w-[20px] h-[20px] text-center flex-shrink-0 text-[#cdcefa] mt-[-8px]">
                                                        <CompanyIconSVG style={{ width: "16px", height: "16px" }} />
                                                    </div>
                                                )}
                                            </div>
                                            <span>{company.name}</span>
                                            <div className={"grow text-right"}>
                                                {
                                                    isActive &&
                                                    <Check size={18} />
                                                }
                                            </div>
                                        </li>
                                    );
                                })
                            }
                        </ul>
                        <div
                            className="flex items-center space-x-2 mt-[3px] text-accent py-[8px] px-[10px] hover:bg-[#F9F9F9] rounded-[8px] cursor-pointer"
                            onClick={handleCreationModalOpen}
                        >
                            <Plus size={16} />
                            <span>
                                {t("buttons.createNewCompany")}
                            </span>
                        </div>
                    </div>
                </Menu>
            </div>

            <CompanyCreationModal
                isOpen={isCreationModalOpen}
                onClose={handleCreationModalClose}
                onCreateCompany={(company) => {
                    handleSelectedCompanyChange(company);
                    handleCreationModalClose();
                }}
            />
        </>
    );
}